import { cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { notify } from "@/modules/notify/notify";
import { authenticatedRouting } from "@/modules/router/routings/authenticatedRouting";
import { Link } from "@/modules/theme/components/Link/Link";
import { Loader } from "@/modules/theme/components/Loader/Loader";
import { PointsLabel } from "@/modules/theme/components/PointsLabel";
import { StarsRating } from "@/modules/theme/components/Stars/StarsRating";
import { useTheme } from "@/modules/theme/hooks/useTheme";
import { useUserDataContext } from "@/modules/user/hooks/useUserDataContext";
import { useCompleteSubtaskMutation } from "@/pages/SubtaskDetailsPage/hooks/useCompleteSubtaskMutation";
import { useUndoSubtaskCompletionMutation } from "@/pages/SubtaskDetailsPage/hooks/useUndoSubtaskCompletionMutation";

import { createTaskItemStyles } from "../hooks/createTaskItemStyles";
import { ReactComponent as Arrow } from "../icons/arrow.svg";
import { ReactComponent as Tick } from "../icons/tick.svg";

import { createSubtaskItemStyles } from "./createSubtaskItemStyles";

export type SerializedSubtaskTaskItem = {
  id: string;
  name: string;
  pointsNumber: number;
  done?: boolean;
  rating?: number | null;
};

export type SubtaskItemProps = {
  className?: string;
} & SerializedSubtaskTaskItem;

const translationNamespace = "pages/TaskDetailsPage";

export const SubtaskItem = ({
  id,
  name,
  pointsNumber,
  done = false,
  rating,
  className,
}: SubtaskItemProps) => {
  const theme = useTheme();
  const { t } = useTranslation(translationNamespace);
  const taskItemClasses = createTaskItemStyles(theme);
  const { currentGroup } = useUserDataContext();
  const completeSubtaskMutation = useCompleteSubtaskMutation();
  const undoSubtaskCompletionMutation = useUndoSubtaskCompletionMutation();
  const classes = createSubtaskItemStyles();

  const areRequestsPending =
    completeSubtaskMutation.status === "loading" ||
    undoSubtaskCompletionMutation.status === "loading";

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentGroup && !done) {
      completeSubtaskMutation.mutate({
        comment: "",
        userGroupId: currentGroup.id,
        subtaskId: id,
      });
    } else {
      notify.alert.error(t("Something went wrong..."));
    }
  };

  return (
    <Link.Internal
      className={cx(taskItemClasses.root, className)}
      to={authenticatedRouting.buildUrl(
        done ? "doneSubtaskDetails" : "undoneSubtaskDetails",
        {
          params: {
            id,
          },
        }
      )}
    >
      <button
        onClick={handleButtonClick}
        disabled={done}
        className={cx(
          taskItemClasses.progress,
          {
            [taskItemClasses.taskDone]: done,
          },
          classes.subtaskButton
        )}
      >
        {areRequestsPending ? (
          <Loader size={16} variant={done ? "secondary" : "primary"} />
        ) : (
          <Tick className={taskItemClasses.tick} />
        )}
      </button>
      <div className={taskItemClasses.content}>
        <div>
          <p className={taskItemClasses.name}>{name}</p>
        </div>
        <div className={taskItemClasses.additionalInfo}>
          <div className={taskItemClasses.ratingWrapper}>
            <PointsLabel
              points={pointsNumber}
              className={taskItemClasses.points}
            />
            {rating && <StarsRating value={rating} size={14} />}
          </div>
        </div>
      </div>
      <div className={taskItemClasses.arrowWrapper}>
        <Arrow />
      </div>
    </Link.Internal>
  );
};
